import { useCallback, useState } from 'react'
import type { SubmitArgs } from '@/context/JobCenterContext'
import { useJobCenter } from '@/context/JobCenterContext'
import type { PriceConfirmInfo } from '@/components/studio'
import { withPromptEdits, type PromptEdits } from '@/lib/promptEdits'

/**
 * 待确认的提交。
 *
 * `args` 允许两种形态：**单个产品传一条**（9 个生成页原本的写法，零改动），
 * **批量表格传一组**（一行产品一条）。这里**不用 `args` / `batch` 两个字段** ——
 * 两个字段就会出现「得先判断这次是哪种形态」的分支，而分支漏一处的表现是
 * 「批量时只提交了第一行」，还不报错。一个字段、在 hook 内归一化成数组，
 * 提交路径只有一条。
 */
export type GeneratePayload = {
  info: PriceConfirmInfo
  args: SubmitArgs | SubmitArgs[]
  /**
   * **附带任务**：与主任务共用这一个决策关口（同一次确认、同一笔总价），
   * 但它们是**各自独立的任务** —— 不同工作流、各自成败、各自计费。
   *
   * 目前唯一的使用方是 `/social-image` 的「图文同出」：一次生成既出图
   * （`social-xhs`）又出配套文案（`copywriting`）。用户的真实场景就是
   * 「发布要图文配套」，拆成两次点击等于让他自己记住「图出完了还要再去配一段」。
   *
   * 三条刻意的选择：
   * ① **不走 `submitBatch`** —— 批次语义是「同一个工作流的 N 行产品」，
   *    右栏会给它聚成一张批次卡并标「批量 · N 个」。图与文案聚成一张卡，
   *    那行数字读起来像「两行产品」，而它们根本不是同一件事。
   * ② **串行提交**（`await` 逐个），与 `submitBatch` 同一个理由：上游网关对并发
   *    提交有限流。`submit` 内部把错误吃进 `state: 'failed'`，所以前一条失败
   *    不会连累后一条。
   * ③ **弹窗里的提示词编辑只回填主任务**。弹窗展示的只有主任务那一份词
   *    （`info.prompt` / `info.shotPrompts`），`edits` 也就只对它有语义；
   *    附带任务的词由发起页从表单派生、没有第二处可编辑入口。
   *    ⚠️ 别顺手把 `edits` 也抄给附带任务 —— 那是**另一份词**，
   *    套上去等于用图的提示词去写文案。
   */
  also?: SubmitArgs[]
}

/**
 * 生成前「报价确认」流程。
 * 参考站的关键交互：点生成不直接扣费，先弹一次报价与提示词确认，
 * 确认后才提交。避免误点造成消耗，也让用户看清将发送什么。
 *
 * **批量也是这一次弹窗**：批量确认与单个确认是同一个决策关口（花多少钱、
 * 发什么词），拆成两个弹窗会让报价行、免费标记、提示词展示各维护两遍。
 * 弹窗内按 `info.batch` 自增殖行清单 —— 交互只有一个入口。
 */
export function useGenerateFlow() {
  const { submit, submitBatch } = useJobCenter()
  const [pending, setPending] = useState<GeneratePayload | null>(null)

  const request = useCallback((payload: GeneratePayload) => setPending(payload), [])
  const cancel = useCallback(() => setPending(null), [])
  /**
   * 确认提交。
   *
   * `edits` = 用户在确认弹窗里**改过的提示词**（`PromptEdits`）。它必须在这里
   * 并回 `args` —— 弹窗里能编辑、发出去的却是模板原文的话，用户改的那一版
   * 等于白改，而且看不出来（预览与实发不是同一份，本项目最不该破的一条）。
   *
   * ⚠️ 编辑**只对单条提交生效**。弹窗里可编辑的两支都是「单产品」形态；
   * 批量那一支是只读的（一行是「该行全部张拼起来的词」，改一处没法回填成 N 张，
   * 见 `PriceConfirmDialog`）。所以 `list.length > 1` 时 `edits` 必然是 `undefined`。
   * 这里仍显式挡住另一种可能：万一以后批量也可编辑、而这里忘了改，
   * **一份词会被抄到每一行** —— 提交、出图、记账全都正常，只有图不对。
   */
  const confirm = useCallback(
    async (edits?: PromptEdits) => {
      const p = pending
      setPending(null)
      if (!p) return
      const list = Array.isArray(p.args) ? p.args : [p.args]
      if (!list.length) return
      const args = edits && list.length === 1 ? [withPromptEdits(list[0], edits)] : list
      // 长度 1 → 走单提交路径（**不生成批次 id**，右栏就不会把它聚成「批量 · 1 个」）；
      // 长度 > 1 → 走批次路径，共用一个批次 id，逐行串行提交、失败不连坐。
      if (args.length === 1) await submit(args[0])
      else await submitBatch(args)
      /*
       * 附带任务（见 `also` 上的说明）：主任务提交完再逐条提交。
       *
       * ⚠️ 顺序是**主任务在前**，不是并发：主任务失败（`state: 'failed'`）时
       * 附带任务照旧提交 —— 两条本来就不互相依赖（图文各自独立），
       * 但按主任务的价格 / 参数是用户刚刚核对过的那一份，
       * 「先发核对过的」这个顺序让人回头查记录时对得上。
       */
      for (const extra of p.also ?? []) await submit(extra)
    },
    [pending, submit, submitBatch],
  )

  return { pending: pending?.info ?? null, request, confirm, cancel }
}
